import React, { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';

import { preloadPhoto, cleanUrl } from '../../../utils/common';
import { getAuthClient } from '../../../utils/auth';
import { buildYourTourApiUrl } from '../../../utils/ApiHelpers';
import useConfigStore from '../../../stores/configStore';

import ConditionalWrapperDivs from '../../HelperComponents/ConditionalWrapperDivs';
import LoadingSpinner from '../../LoadingSpinner/LoadingSpinner';

import FadingContainer from '../../FadingContainer/FadingContainer';
import PhotoGalleryImage from '../PhotoGalleryImage/PhotoGalleryImage';

import './PhotoGalleryDetail.scss';

const auth = getAuthClient();

const PHOTO_GALLERY_INSTRUCTIONS = 'Use the arrows to browse photos.';

const SLIDESHOW_ADVANCE_MS = 6000;

// How long a kiosk sits untouched after a manual arrow tap before the slideshow
// takes over again and returns to the first slide. Five advance intervals: long
// enough that someone browsing at a normal pace is not interrupted mid-look,
// short enough that the next visitor never meets a kiosk parked on slide seven.
const SLIDESHOW_IDLE_RESET_MS = SLIDESHOW_ADVANCE_MS * 5;

const PhotoGalleryDetail = ({
  id, className, uuid, images, galleryClass, imageClass, isLoading, showInstructions,
}) => {
  // All hooks must be called unconditionally (React Rules of Hooks)
  const [photoGalleryData, setPhotoGalleryData] = useState(null);
  const [firstPicIsLoaded, setFirstPicIsLoaded] = useState(false);
  const [backgroundImage, setBackgroundImage] = useState(null);
  const [showControls, setShowControls] = useState(false);
  const [isHomescreenSlideshow, setIsHomescreenSlideshow] = useState(null);
  const [photos, setPhotos] = useState(null);
  const [formattedPhotos, setFormattedPhotos] = useState([]);
  const [activePhotoIndex, setActivePhotoIndex] = useState(0);
  // Counts manual arrow taps on the homescreen slideshow; null when nobody has
  // touched it (or the idle timer has since fired). A counter rather than a
  // timestamp so two taps in the same millisecond still restart the idle timer.
  const [interactionCount, setInteractionCount] = useState(null);
  const mountedRef = useRef(true);
  const featureFlags = useConfigStore((state) => state.featureFlags);
  const { platformShowHomescreenGalleryControls } = featureFlags || {};

  useEffect(() => {
    setIsHomescreenSlideshow(galleryClass === 'homescreenSlideshow');
  }, [galleryClass]);

  // Show controls during loading or when we have multiple photos. The homescreen
  // slideshow only gets controls if the community has opted in.
  useEffect(() => {
    const isLoadingPhotos = photos === null || isHomescreenSlideshow === null;
    const allowsControls = !isHomescreenSlideshow || platformShowHomescreenGalleryControls;
    const hasMultiplePhotos = photos?.length > 1 && allowsControls;
    setShowControls(isLoadingPhotos || hasMultiplePhotos);
  }, [photos, isHomescreenSlideshow, platformShowHomescreenGalleryControls]);

  useEffect(() => {
    let isMounted = true;
    if (uuid && isHomescreenSlideshow !== null) {
      const fetchData = async () => {
        // Use full_screen style for homescreen slideshow, modal for everything else
        const imageStyle = isHomescreenSlideshow ? 'full_screen' : 'modal';
        const url = buildYourTourApiUrl(`/your_tour_api/photo_gallery/${uuid}`, { image_style: imageStyle });
        const data = await auth.fetchWithAuthentication(url, 'GET', null);
        if (isMounted && data?.images) {
          setPhotoGalleryData(data);
        }
      };
      fetchData();
    }
    return () => {
      isMounted = false;
    };
  }, [uuid, isHomescreenSlideshow]);

  useEffect(() => {
    if (photoGalleryData?.images) {
      setPhotos(photoGalleryData.images);
    }
  }, [photoGalleryData]);

  useEffect(() => {
    if (images) {
      setPhotos(images);
    }
  }, [images]);

  // Format photos for display (clean URLs)
  useEffect(() => {
    if (photos?.length > 0) {
      const rawFormattedPhotos = [];
      // eslint-disable-next-line no-restricted-syntax
      for (const photo of photos) {
        // New API returns image as string, old format has image.modal/fullScreen
        const image = typeof photo.image === 'string'
          ? photo.image
          : (isHomescreenSlideshow ? photo.image.fullScreen : photo.image.modal);
        const formattedPhoto = {
          uuid: photo.id || photo.uuid,
          caption: photo.caption,
          formattedImage: cleanUrl(image),
        };
        rawFormattedPhotos.push(formattedPhoto);
      }
      setFormattedPhotos(rawFormattedPhotos);
    }
  }, [photos, isHomescreenSlideshow]);

  // Preload the first gallery photo and set as background image
  useEffect(() => {
    const handlePreloadingPhotos = async (firstImage) => {
      await preloadPhoto(firstImage);
      if (!mountedRef.current) return;
      setFirstPicIsLoaded(true);
    };
    if (formattedPhotos?.length > 0) {
      const firstImage = formattedPhotos[0].formattedImage;
      setBackgroundImage(firstImage);
      handlePreloadingPhotos(firstImage);
    }
  }, [formattedPhotos]);

  // Preload upcoming slideshow images in background for smooth transitions
  useEffect(() => {
    if (!isHomescreenSlideshow || !firstPicIsLoaded || formattedPhotos.length <= 1) return;

    // Preload next few images after first image is loaded
    const preloadUpcoming = async () => {
      // Preload images 2-4 (indices 1-3) in background
      const imagesToPreload = formattedPhotos.slice(1, 4);
      await Promise.all(imagesToPreload.map((photo) => preloadPhoto(photo.formattedImage)));
    };

    // Use requestIdleCallback to avoid blocking UI
    if (typeof requestIdleCallback !== 'undefined') {
      requestIdleCallback(preloadUpcoming, { timeout: 3000 });
    } else {
      setTimeout(preloadUpcoming, 100);
    }
  }, [isHomescreenSlideshow, firstPicIsLoaded, formattedPhotos]);

  useEffect(
    () => () => {
      mountedRef.current = false;
    },
    [],
  );

  useEffect(() => {
    setActivePhotoIndex(0);
    setInteractionCount(null);
  }, [formattedPhotos]);

  // Automatic slideshow for homescreen. It runs whether or not manual controls
  // are shown; a manual tap only pauses it for the idle window below.
  useEffect(() => {
    if (isHomescreenSlideshow && formattedPhotos.length > 0 && interactionCount === null) {
      const myInterval = setInterval(() => {
        setActivePhotoIndex((prevIndex) => (prevIndex + 1) % formattedPhotos.length);
      }, SLIDESHOW_ADVANCE_MS);
      return () => {
        clearInterval(myInterval);
      };
    }
    return undefined;
  }, [isHomescreenSlideshow, formattedPhotos.length, interactionCount]);

  // After the last manual tap, an idle kiosk returns to the first slide and the
  // auto-advance above starts again.
  useEffect(() => {
    if (interactionCount === null) return undefined;
    const idleTimer = setTimeout(() => {
      setActivePhotoIndex(0);
      setInteractionCount(null);
    }, SLIDESHOW_IDLE_RESET_MS);
    return () => {
      clearTimeout(idleTimer);
    };
  }, [interactionCount]);

  // A tap on the homescreen slideshow restarts the idle timer rather than
  // ending the slideshow. Other galleries are unaffected.
  const registerInteraction = () => {
    if (isHomescreenSlideshow) {
      setInteractionCount((prev) => (prev === null ? 1 : prev + 1));
    }
  };

  const handleRightClick = () => {
    registerInteraction();
    setActivePhotoIndex((prevIndex) => (
      prevIndex === formattedPhotos.length - 1 ? 0 : prevIndex + 1
    ));
  };

  const handleLeftClick = () => {
    registerInteraction();
    setActivePhotoIndex((prevIndex) => (
      prevIndex === 0 ? formattedPhotos.length - 1 : prevIndex - 1
    ));
  };

  // Return null if we have no way to get images and we're not in a loading state
  if ((!images || images.length === 0) && !uuid && !isLoading) {
    return null;
  }

  return (
    <FadingContainer>
      <div
        id={id || ''}
        className={className || ''}
      >

        <div className={galleryClass || ''}>
          {showControls && (
            <>
              <button
                type="button"
                className="slideshowControls slideshowControls-left"
                onClick={handleLeftClick}
                disabled={photos === null}
              >
                <span className="arrow">
                  <span />
                  <span />
                </span>
                <span className="sr-only">Previous</span>
              </button>

              <button
                type="button"
                className="slideshowControls slideshowControls-right"
                onClick={handleRightClick}
                disabled={photos === null}
              >
                <span className="arrow">
                  <span />
                  <span />
                </span>
                <span className="sr-only">Next</span>
              </button>
            </>
          )}

          <ConditionalWrapperDivs
            condition={!isHomescreenSlideshow}
            classNamesArray={['slideshowContainer']}
          >
            {!isHomescreenSlideshow && (
            <div
              className="slideshowBackground"
              style={{ backgroundImage: `url(${backgroundImage})` }}
            />
            )}

            <ConditionalWrapperDivs condition={!isHomescreenSlideshow} classNamesArray={[]}>

              {!firstPicIsLoaded && <LoadingSpinner />}

              {formattedPhotos?.length > 0
              && formattedPhotos.map((photo, i) => (
                <PhotoGalleryImage
                  key={photo.uuid}
                  uuid={photo.uuid || photo.id}
                  image={photo.formattedImage}
                  caption={photo.caption}
                  className={`${imageClass} ${i === activePhotoIndex && firstPicIsLoaded ? 'slideshowPhoto--active' : ''
                  }`}
                  count={formattedPhotos.length}
                  index={i}
                />
              ))}
            </ConditionalWrapperDivs>
          </ConditionalWrapperDivs>

        </div>
        {showInstructions && formattedPhotos?.length > 1 && (
          <div className="detail__footnote">
            {PHOTO_GALLERY_INSTRUCTIONS}
          </div>
        )}
      </div>

    </FadingContainer>
  );
};

PhotoGalleryDetail.propTypes = {
  id: PropTypes.string,
  className: PropTypes.string,
  uuid: PropTypes.string,
  showInstructions: PropTypes.bool,
  images: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      uuid: PropTypes.string,
      caption: PropTypes.string,
      // Supports both new API format (string) and legacy format (object)
      image: PropTypes.oneOfType([PropTypes.string,
        PropTypes.shape({
          fullScreen: PropTypes.string,
          modal: PropTypes.string,
        })]),
    }),
  ),
  galleryClass: PropTypes.string,
  imageClass: PropTypes.string,
  isLoading: PropTypes.bool,
};

PhotoGalleryDetail.defaultProps = {
  id: null,
  className: null,
  uuid: null,
  showInstructions: false,
  images: null,
  galleryClass: null,
  imageClass: null,
  isLoading: false,
};

export default PhotoGalleryDetail;
